export const MAX_IMAGE_DIMENSION = 1500;
export const IMAGE_QUALITY = 0.82;

export function getContainedDimensions(width, height, maxDimension = MAX_IMAGE_DIMENSION) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error('The selected image has invalid dimensions.');
  }

  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function getOutputFileName(fileName, extension) {
  const baseName = String(fileName || 'fraemohs-home').replace(/\.[^.]+$/, '');
  return `${baseName}.${extension}`;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function loadImageSource(file) {
  if (typeof window.createImageBitmap === 'function') {
    try {
      const bitmap = await window.createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, cleanup: () => bitmap.close() };
    } catch (error) {
      // Fall back to an HTMLImageElement for formats the browser can decode there.
    }
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      cleanup: () => URL.revokeObjectURL(objectUrl),
    });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('This image format could not be read. Please choose a JPEG, PNG, WebP, or browser-supported HEIC image.'));
    };
    image.src = objectUrl;
  });
}

/**
 * Removes metadata, constrains the longest side to 1500px, and converts the
 * upload to efficient WebP (with JPEG fallback) before it reaches Parse.
 */
export async function sanitizeImageFile(file) {
  if (!file || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }

  const loadedImage = await loadImageSource(file);

  try {
    const dimensions = getContainedDimensions(loadedImage.width, loadedImage.height);
    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser could not prepare the image for upload.');

    context.drawImage(loadedImage.source, 0, 0, dimensions.width, dimensions.height);

    const webpBlob = await canvasToBlob(canvas, 'image/webp', IMAGE_QUALITY);
    if (webpBlob?.type === 'image/webp') {
      return new File([webpBlob], getOutputFileName(file.name, 'webp'), { type: 'image/webp' });
    }

    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', IMAGE_QUALITY);
    if (!jpegBlob) throw new Error('This browser could not prepare the image for upload.');

    return new File([jpegBlob], getOutputFileName(file.name, 'jpg'), { type: 'image/jpeg' });
  } finally {
    loadedImage.cleanup();
  }
}
